import { motion, AnimatePresence } from "framer-motion";

const dallasOfferOptions = [
  {
    label: "30% Discount",
    desc: "30% off your final project rate. Includes strategy, sitemap planning, 5–10 custom-designed pages, Framer build, basic CMS setup, and on-site SEO structure.",
  },
  {
    label: "$1,000 One-Pager",
    desc: "A focused, conversion-ready single-page site at a flat rate. Includes structured layout, mobile-optimized Framer build, contact form integration, and basic SEO setup.",
  },
  {
    label: "Barter / Trade",
    desc: "We take on a small number of projects through trade in lieu of full payment.\nAt the moment, we're prioritizing experiential and service-based exchanges: high-end dinners, boutique hotel stays, private events, concerts, beauty services, and select professional support (legal, accounting).\n\nIf you think you offer something we'd genuinely use or enjoy, it's worth reaching out.",
  },
];

interface StepLocalsOnlyProps {
  isDallasLocal: boolean;
  setIsDallasLocal: (v: boolean) => void;
  dallasOfferType: string;
  setDallasOfferType: (v: string) => void;
  onViewOfferDetails?: (offerType: string) => void;
}

const StepLocalsOnly = ({
  isDallasLocal,
  setIsDallasLocal,
  dallasOfferType,
  setDallasOfferType,
  onViewOfferDetails,
}: StepLocalsOnlyProps) => {
  // null = not answered, true = yes, false = no
  const answered = isDallasLocal === true ? true : isDallasLocal === false && dallasOfferType === "__no__" ? false : null;

  const handleYes = () => {
    setIsDallasLocal(true);
    setDallasOfferType("");
  };

  const handleNo = () => {
    setIsDallasLocal(false);
    setDallasOfferType("__no__");
  };

  return (
    <div className="flex flex-col gap-6 px-6 py-5">
      <div>
        <p className="font-heading text-ts-base text-foreground mb-1">
          "Locals Only" Club
        </p>
        <p className="font-body text-ts-xs text-foreground/60">
          Are you a Dallas native?
        </p>
      </div>

      {/* Yes / No toggle buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleYes}
          className={`flex-1 px-4 py-3 border font-body text-ts-2xs font-medium tracking-[0.1em] uppercase transition-all duration-200 ${
            isDallasLocal && answered === true
              ? "bg-primary text-primary-foreground border-primary"
              : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
          }`}
        >
          Yes
        </button>
        <button
          type="button"
          onClick={handleNo}
          className={`flex-1 px-4 py-3 border font-body text-ts-2xs font-medium tracking-[0.1em] uppercase transition-all duration-200 ${
            answered === false
              ? "bg-primary text-primary-foreground border-primary"
              : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
          }`}
        >
          No
        </button>
      </div>

      {/* No response */}
      <AnimatePresence mode="wait">
        {answered === false && (
          <motion.div
            key="no-response"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="border-l-2 border-border pl-3 py-2"
          >
            <p className="font-body text-ts-xs text-foreground/50 italic">
              oh... okay, well. you don't need to do anything here.
            </p>
          </motion.div>
        )}

        {/* Yes - show offer selection */}
        {isDallasLocal && answered === true && (
          <motion.div
            key="yes-offers"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="flex flex-col gap-4"
          >
            <div>
              <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-2 block">
                Choose your offer
              </label>
              <div className="flex flex-wrap gap-1.5">
                {dallasOfferOptions.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() =>
                      setDallasOfferType(dallasOfferType === opt.label ? "" : opt.label)
                    }
                    className={`px-3 py-1.5 border font-body text-ts-2xs tracking-wide transition-all duration-200 ${
                      dallasOfferType === opt.label
                        ? "bg-primary text-primary-foreground border-primary scale-[1.03]"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <AnimatePresence>
              {dallasOfferType && dallasOfferType !== "__no__" && (() => {
                const selected = dallasOfferOptions.find((o) => o.label === dallasOfferType);
                if (!selected) return null;
                return (
                  <motion.div
                    key={selected.label}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.25 }}
                    className="border-l-2 border-primary/30 pl-3 py-1"
                  >
                    <p className="font-body text-[11px] text-foreground/60 leading-relaxed whitespace-pre-line">
                      {selected.desc}
                    </p>
                    <button
                      type="button"
                      onClick={() => onViewOfferDetails?.(dallasOfferType)}
                      className="mt-2 font-body text-ts-2xs font-medium text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
                    >
                      View details
                    </button>
                  </motion.div>
                );
              })()}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StepLocalsOnly;
